import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';

function Layout({ children }) {
  const { user, logout, isAdmin, isGuide, isPoolSupervisor } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstall, setShowInstall] = useState(false);

  // PWA Install prompt
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setShowInstall(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Geo tracking (silent - only sends to server for admin to see)
  useEffect(() => {
    if (!navigator.geolocation) return;
    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        api.geoCheckin(pos.coords.latitude, pos.coords.longitude);
      },
      () => {},
      { enableHighAccuracy: true, maximumAge: 60000, timeout: 10000 }
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    await installPrompt.userChoice;
    setShowInstall(false);
    setInstallPrompt(null);
  };

  const navItems = isAdmin ? [
    { path: '/', icon: '🏠', label: 'Accueil' },
    { path: '/groups', icon: '👥', label: 'Groupes' },
    { path: '/search', icon: '🔍', label: 'Recherche' },
    { path: '/guides', icon: '🧭', label: 'Guides' },
    { path: '/stats', icon: '📊', label: 'Stats' },
    { path: '/settings', icon: '⚙️', label: 'Réglages' },
    { path: '/map', icon: '🗺️', label: 'Map' },
    { path: '/complaints', icon: '🚨', label: 'Incidents' },
    { path: '/attendance', icon: '📋', label: 'Présence' }
  ] : isGuide ? [
    { path: '/', icon: '🏠', label: 'Accueil' },
    { path: '/groups', icon: '👥', label: 'Groupes' },
    { path: '/search', icon: '🔍', label: 'Recherche' },
    { path: '/stats', icon: '📊', label: 'Stats' },
    { path: '/settings', icon: '⚙️', label: 'Réglages' },
    { path: '/complaints', icon: '🚨', label: 'Incidents' },
    { path: '/attendance', icon: '📋', label: 'Présence' }
  ] : [
    // Pool Supervisor: Dashboard only
    { path: '/', icon: '🏠', label: 'Accueil' }
  ];

  return (
    <div style={{ minHeight: '100vh', paddingBottom: '70px' }}>
      {/* Header */}
      <header style={{
        background: '#111',
        borderBottom: '1px solid #333',
        padding: '12px 16px',
        position: 'sticky',
        top: 0,
        zIndex: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <img src="/logo-sunskycamp.png" alt="SunSkyCamp logo" style={{ height: '36px' }} />
          <div>
            <div style={{ fontWeight: 700, fontSize: '14px' }}>Sun Sky Camp</div>
            <div style={{ fontSize: '11px', color: 'var(--text-gray)' }}>{user?.name} · {user?.role}</div>
          </div>
        </div>
        <button onClick={logout} style={{
          background: 'none', border: 'none', color: 'var(--text-gray)',
          cursor: 'pointer', padding: '8px', fontSize: '18px'
        }}>⏻</button>
      </header>

      {/* Content */}
      <main style={{ padding: '16px', maxWidth: '800px', margin: '0 auto' }}>
        {children}
      </main>

      {/* PWA Install Banner */}
      {showInstall && (
        <div className="install-banner">
          <span style={{ fontSize: '13px' }}>📲 Installer l'application</span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={handleInstall} style={{
              background: 'var(--primary)', color: 'white', border: 'none',
              padding: '6px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 600, cursor: 'pointer'
            }}>Installer</button>
            <button onClick={() => setShowInstall(false)} style={{
              background: 'none', border: '1px solid #555', color: 'var(--text-gray)',
              padding: '6px 12px', borderRadius: '8px', fontSize: '12px', cursor: 'pointer'
            }}>Plus tard</button>
          </div>
        </div>
      )}

      {/* Bottom Navigation */}
      <nav style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        background: '#111', borderTop: '1px solid #333',
        display: 'flex', justifyContent: 'space-around',
        padding: '8px 0', zIndex: 50
      }}>
        {navItems.map(item => (
          <button key={item.path} onClick={() => navigate(item.path)} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px',
            color: location.pathname === item.path ? 'var(--primary)' : 'var(--text-gray)',
            fontSize: '10px', fontWeight: location.pathname === item.path ? 700 : 400,
            padding: '4px 8px'
          }}>
            <span style={{ fontSize: '20px' }}>{item.icon}</span>
            <span>{item.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}

export default Layout;
